import { APIService, RequestBody, RequestHeaders } from '../api/apiService.js';
import {
  CreateFileFromTemplatePayload,
  CreateFolderPayload,
  CreateTagPayload,
  DeleteFileAccessPayload,
  GetFileAccessesPayload,
  GetFileAccessesResponse,
  GetFilesPayload,
  GetFilesResponse,
  GetViewerResponse,
  RevokeDenyFileAccessPayload,
  SellFilePayload,
  UpdateFileAccessPayload,
} from '../interfaces/FileService';
import FilePermissions from '../interfaces/FilePermissions';
import File from '../interfaces/File';
import Tag from '../interfaces/Tag';
import qs from 'qs';
import FileDetail from '../interfaces/FileDetail';

export class FileService extends APIService {
  /**
   * @desc Get files
   * @param payload
   * @return {Promise<* | undefined>}
   */
  getFiles(payload: GetFilesPayload): Promise<GetFilesResponse> {
    return this.request('/myfiles', 'POST', { ...payload });
  }

  /**
   * Retrieves the details of the specified file.
   *
   * @param {string | number} fileId - The unique identifier of the file to retrieve.
   * @return {Promise<Object>} A promise that resolves to the file details.
   */
  getFilePermissions(fileId: string | number): Promise<FilePermissions> {
    return this.request(`/file/request/v2/${fileId}`, 'GET');
  }

  /**
   * Retrieves a file from the server based on the provided file ID.
   *
   * @param {string|number} fileId - The unique identifier for the file. Can be either a string or a number.
   * @return {Promise} A promise that resolves with the server's response containing the file data.
   */
  getFile(fileId: string | number): Promise<FileDetail> {
    return this.request(`/myfiles/${fileId}`, 'GET');
  }

  /**
   * Retrieves information for a specific file based on the given file ID.
   * @deprecated
   * @param {string | number} fileId - The unique identifier for the file.
   * @return {Promise<Object>} A promise that resolves to an object containing file information.
   */
  getFileInfo(fileId: string | number): Promise<FilePermissions> {
    return this.request(`/file/info/${fileId}`, 'GET');
  }

  /**
   * Retrieves a one-time password (OTP) for a specified file.
   *
   * @param {number} fileUid - The unique identifier of the file for which the OTP is requested.
   * @return {Promise} A promise that resolves with the OTP details.
   */
  getFileOtp(fileUid: number) {
    return this.request(`/myfiles/otp/${fileUid}`, 'GET');
  }

  /**
   * Moves files to a specified parent directory.
   *
   * @param {string | number} parent - The ID or name of the parent directory where the files should be moved.
   * @param {string[]} fileIds - An array of file IDs to be moved to the specified parent directory.
   * @return {Promise} A promise that resolves with the server response after the files are moved.
   */
  moveFiles(parent: string | number | null, fileIds: string[]): Promise<void> {
    return this.request(
      `/myfiles/move${parent ? `?parent=${parent}` : ''}`,
      'PUT',
      fileIds
    );
  }

  /**
   * Duplicates the specified files with a given prefix.
   *
   * @param {string} prefix - The prefix to be added to the duplicated file names.
   * @param {string[]} fileIds - An array of file IDs representing the files to be duplicated.
   * @return {Promise<Response>} A promise that resolves to the server response after duplicating the files.
   */
  duplicateFiles(
    fileIds: string[],
    prefix?: string
  ): Promise<GetFilesResponse> {
    let url = '/myfiles/duplicate';
    if (prefix !== null) {
      url += `?prefix=${prefix}`;
    }

    return this.request(url, 'POST', fileIds);
  }

  /**
   * Extracts content from a PDF file identified by the unique file identifier (fileUid).
   *
   * @param {number} fileUid - The unique identifier of the PDF file to be extracted.
   * @param {RequestHeaders} [headers] - Optional HTTP headers to include in the request.
   * @return {Promise<Response>} A promise that resolves to the response of the extraction request.
   */
  getPdfExtraction(
    fileUid: number,
    headers?: RequestHeaders
  ): Promise<Response> {
    return this.request(
      `/api/PdfExtraction/${fileUid}`,
      'GET',
      undefined,
      headers,
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL
    );
  }

  /**
   * Fetches the extraction link for a PDF file using the provided file unique identifier.
   *
   * @param {number} fileUid - The unique identifier of the PDF file to extract the link for.
   * @param {RequestHeaders} [headers] - Optional headers to include in the HTTP request.
   * @return {Promise<Response>} A promise that resolves to the HTTP response containing the PDF extraction link.
   */
  getPdfExtractionLink(
    fileUid: number,
    headers?: RequestHeaders
  ): Promise<Response> {
    return this.request(
      `/api/pdfextractionlink/${fileUid}`,
      'GET',
      undefined,
      headers,
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL
    );
  }

  /**
   * Downloads a PDF file based on the specified parameters.
   *
   * @param {string} fileId - The unique identifier of the file to be downloaded.
   * @param {number} isDownloadFree - Flag to determine the type of download; 0 for free download, otherwise for paid extraction.
   * @param {number} [onBehalfOfUserId] - Optional user ID to specify the request is made on behalf of another user.
   * @param {RequestHeaders} [headers] - Optional headers to pass along with the request.
   * @return {Promise<Response>} A promise that resolves to the response of the PDF download request.
   */
  downloadPdf(
    fileId: string,
    isDownloadFree: number,
    onBehalfOfUserId?: number,
    headers?: RequestHeaders
  ): Promise<Response> {
    const endpoint = `${isDownloadFree === 0 ? '/api/PdfDownload/' : '/api/PdfExtraction/'}${fileId}?DownloadFree=${isDownloadFree}${onBehalfOfUserId ? `&onBehalfOf=${onBehalfOfUserId}` : ''}`;
    return this.request(
      endpoint,
      'GET',
      undefined,
      headers,
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL
    );
  }

  /**
   * Retrieves the viewer response for a specific file identified by its unique ID.
   *
   * @param {number} fileUid - The unique identifier of the file for which the viewer response is requested.
   * @param {string} token - The authentication token used to authorize the request.
   * @return {Promise<Response>} A promise that resolves to the response object containing the viewer information.
   */
  getViewer(fileUid: number, token: string): Promise<GetViewerResponse> {
    return this.request(
      `/api/viewer/${fileUid}/${token}`,
      'GET',
      undefined,
      {},
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL
    );
  }

  /**
   * @desc Get tags
   * @return {Promise<* | undefined>}
   * @param onBehalfOfUserId
   */
  getTags(onBehalfOfUserId?: number): Promise<Tag[]> {
    return this.request(
      `/myfiles/tags${onBehalfOfUserId ? `?onBehalfOf=${onBehalfOfUserId}` : ''}`,
      'GET'
    );
  }

  /**
   * Creates a new file based on a specified template.
   *
   * @param {CreateFileFromTemplatePayload} payload - The data required to create the file from the template, including template details and file attributes.
   * @return {Promise<File>} A promise that resolves with the created file object.
   */
  createFileFromTemplate(
    payload: CreateFileFromTemplatePayload
  ): Promise<File> {
    return this.request('/myfiles/newtemplate', 'POST', { ...payload });
  }

  /**
   * @desc Create a tag
   * @param payload
   * @return {Promise<* | undefined>}
   */
  createTag(payload: CreateTagPayload): Promise<Tag> {
    return this.request('/myfiles/tag', 'POST', { ...payload });
  }

  /**
   * Creates a shortcut for a file or resource specified by the given ID.
   *
   * @param {string | number} id - The identifier of the file or resource for which the shortcut needs to be created.
   * @param parentId
   * @return {Promise<Response>} A Promise that resolves to the response object of the shortcut creation request.
   */
  createShortcut(
    id: string | number,
    parentId?: string | number | undefined
  ): Promise<File> {
    return this.request(`/myfiles/shortcut/${id}`, 'PUT', parentId?.toString());
  }

  /**
   * @desc Delete a tag
   * @param id
   * @return {Promise<* | undefined>}
   */
  deleteTag(id: number): Promise<void> {
    return this.request(`/myfiles/tag/${id}`, 'DELETE');
  }

  /**
   * @desc Update file tag
   * @param fileId
   * @param tagIds
   * @return {Promise<* | undefined>}
   */
  updateFileTags(fileId: string | number, tagIds: number[]): Promise<Response> {
    return this.request(`/myfiles/tag/${fileId}`, 'PUT', tagIds);
  }

  /**
   * Updates the description of a file based on the provided file ID.
   *
   * @param fileUid
   * @param {string} description - The new description to assign to the file.
   * @return {Promise<Response>} A promise that resolves with the server response after updating the file's description.
   */
  updateFileDescription(
    fileUid: number,
    description: string
  ): Promise<Response> {
    return this.request(`/myfiles/description/${fileUid}`, 'PUT', description);
  }

  /**
   * @desc Update favourite files
   * @param fileIds
   * @param value
   * @return {Promise<* | undefined>}
   */
  updateFavouriteFiles(fileIds: string[], value: boolean): Promise<void> {
    return this.request(`/myfiles/favorite?value=${value}`, 'PUT', fileIds);
  }

  /**
   * Updates the archived status of a list of files.
   *
   * @param {string[]} fileIds - An array of file IDs representing the files to update.
   * @param {boolean} value - A boolean indicating the archived status to set for the files.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  updateArchivedFiles(fileIds: string[], value: boolean): Promise<void> {
    return this.request(`/myfiles/archive?value=${value}`, 'PUT', fileIds);
  }

  /**
   * @desc Update deleted files
   * @param fileIds
   * @param value
   * @return {Promise<* | undefined>}
   */
  updateDeletedFiles(fileIds: string[], value: boolean): Promise<void> {
    return this.request(`/myfiles/delete?value=${value}`, 'PUT', fileIds);
  }

  /**
   * @desc Delete files from trash
   * @param fileIds
   * @return {Promise<* | undefined>}
   */
  deleteFiles(fileIds: string[]): Promise<void> {
    // Empty value in qs true/false
    return this.request(`/myfiles/delete`, 'DELETE', fileIds);
  }

  /**
   * @desc Create a folder
   * @param payload
   * @return {Promise<* | undefined>}
   */
  createFolder(payload: CreateFolderPayload): Promise<File> {
    return this.request('/myfiles/newfolder', 'POST', { ...payload });
  }

  /**
   * Renames a file with the given file ID to the specified name.
   *
   * @param {string | number} fileId - The unique identifier of the file to be renamed.
   * @param {string} name - The new name for the file.
   * @return {Promise<Response>} A promise that resolves with the response of the rename operation.
   */
  renameFile(fileId: string | number, name: string): Promise<Response> {
    return this.request(`/myfiles/rename/${fileId}`, 'PUT', { name });
  }

  /**
   * Uploads a file to the server.
   *
   * @param {RequestBody} payload - The file and associated metadata to be uploaded.
   * @return {Promise<Response>} A promise that resolves with the server response.
   */
  uploadFile(payload: FormData): Promise<Response> {
    return this.request(
      '/api/fileupload',
      'POST',
      payload,
      {},
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL
    );
  }

  /**
   * Retrieves the count of unread files.
   *
   * Sends a GET request to the endpoint responsible for counting unread files.
   *
   * @return {Promise<number>} A promise that resolves to the number of unread files.
   */
  getCountFilesUnread() {
    return this.request('/file/countinfilesunread', 'GET');
  }

  /**
   * Searches for files based on the provided criteria in the payload.
   *
   * @param {RequestBody} payload - The criteria used to search for the files.
   * @return {Promise<Response>} - A promise that resolves with the search results.
   */
  searchFiles(payload: RequestBody) {
    return this.request('/myfiles/search', 'POST', payload);
  }

  /**
   * Method to check if files exist for the given payload and optional parent ID.
   *
   * @param fileNames - An array of file names to check for existence.
   * @param {string | null} parentId - The optional parent ID to narrow the scope of the file existence check.
   * @param onBehalfOfUserId
   * @return {Promise<Response>} A promise that resolves to the response of the existence check.
   */
  checkFilesExists(
    fileNames: string[],
    parentId?: number | null,
    onBehalfOfUserId?: number
  ): Promise<File[]> {
    const stringifiesParams = qs.stringify(
      {
        parent: parentId,
        onBehalfOf: onBehalfOfUserId,
      },
      { skipNulls: true }
    );
    return this.request(
      `/myfiles/exists${stringifiesParams ? `?${stringifiesParams}` : ''}`,
      'POST',
      fileNames
    );
  }

  /**
   * Retrieves the list of file accesses.
   *
   * @param {RequestBody} payload - The request body containing necessary parameters.
   * @return {Promise<any>} A promise that resolves with the file accesses data.
   */
  getFileAccesses(
    payload: GetFileAccessesPayload
  ): Promise<GetFileAccessesResponse> {
    return this.request(`/file/listaccesses`, 'POST', { ...payload });
  }

  /**
   * Updates the access permissions for a specified file using the provided payload.
   *
   * @param {UpdateFileAccessPayload} payload - The data structure containing file access information that needs to be updated.
   * @return {Promise<Response>} A promise that resolves to the response of the update request.
   */
  updateFileAccess(payload: UpdateFileAccessPayload): Promise<Response> {
    return this.request(`/file/access/v2`, 'PUT', { ...payload });
  }

  /**
   * Deletes the access permissions to a specified file.
   *
   * @param {RequestBody} payload - The payload containing information about the file and the access permissions to be deleted.
   * @return {Promise<Response>} A promise that resolves to the response of the delete request.
   */
  deleteFileAccess(payload: DeleteFileAccessPayload): Promise<Response> {
    return this.request(`/file/access/v2`, 'DELETE', { ...payload });
  }

  /**
   * When 
   *
   * @param {RequestBody} payload - The payload containing information about the file and the access permissions to be deleted.
   * @return {Promise<Response>} A promise that resolves to the response of the delete request.
   */
  revokeDeniedFileAccess(payload: RevokeDenyFileAccessPayload): Promise<Response> {
     return this.request(`/file/revokedenied`, 'PUT', { ...payload });
  }
  
 /**
   * When 
   *
   * @param {RequestBody} payload - The payload containing information about the file and the access permissions to be deleted.
   * @return {Promise<Response>} A promise that resolves to the response of the delete request.
   */
  sellFile(payload: SellFilePayload){
    return this.request(`/myfiles/sale`, 'POST', { ...payload });
  }

  getSellFile(id : string){
    return this.request(`/file/forsale/${id}`, 'GET');
  }

  getSalesInfo(){
    return this.request(`/dashboard/sales-info`, 'GET');
  }

  uploadSalesImage(fileId: string | number, formData: FormData){
     return this.request(`/transfer/upload/sale/preview/${fileId}`, 'POST', formData);
  }

  removeFileFromStore(fileId: string | number){
     return this.request(`/file/forsale/remove/${fileId}`, 'DELETE');
  }

}
